/**
 * Logging configuration for the Coldsea Deploy frontend application.
 * This provides centralized configuration for logging behavior across the application.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LoggingConfig {
  // Minimum log level to display/store
  level: LogLevel;

  // Whether to log to console
  console: {
    enabled: boolean;
    // Override log level for console (optional)
    level?: LogLevel;
    // Include timestamp in console logs
    includeTimestamp: boolean;
    // Include log level in console output
    includeLevel: boolean;
  };

  // In-memory log storage
  storage: {
    enabled: boolean;
    // Maximum number of logs to keep in memory
    maxEntries: number;
    // Whether to persist logs to localStorage
    persist: boolean;
    // Key for localStorage persistence
    persistKey: string;
  };

  // Remote logging (future enhancement)
  remote: {
    enabled: boolean;
    endpoint?: string;
    // Batch size for sending logs
    batchSize?: number;
    // Interval for sending logs (ms)
    flushInterval?: number;
  };

  // Context to include with every log
  defaultContext: {
    // Application version
    version?: string;
    // Environment name
    environment?: string;
    // Additional static context
    [key: string]: unknown;
  };

  // Log formatting
  format: {
    // Whether to stringify objects
    stringifyObjects: boolean;
    // Max depth for object stringification
    maxObjectDepth: number;
    // Whether to include stack traces for errors
    includeStackTrace: boolean;
  };

  // Performance settings
  performance: {
    // Whether to log performance metrics
    enabled: boolean;
    // Threshold for slow operations (ms)
    slowThreshold: number;
  };

  // Filtering
  filters: {
    // Patterns to exclude from logging
    exclude?: RegExp[];
    // Patterns to include in logging
    include?: RegExp[];
    // Specific modules to filter
    modules?: {
      [module: string]: LogLevel;
    };
  };
}

// Log level hierarchy
export const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Default configuration
export const DEFAULT_CONFIG: LoggingConfig = {
  level: 'info',
  console: {
    enabled: true,
    includeTimestamp: true,
    includeLevel: true,
  },
  storage: {
    enabled: true,
    maxEntries: 1000,
    persist: false,
    persistKey: 'coldsea_deploy_logs',
  },
  remote: {
    enabled: false,
  },
  defaultContext: {
    version: (import.meta.env['VITE_APP_VERSION'] as unknown as string | undefined) || '1.0.0',
    environment: (import.meta.env.MODE as unknown as string | undefined) || 'development',
  },
  format: {
    stringifyObjects: true,
    maxObjectDepth: 3,
    includeStackTrace: true,
  },
  performance: {
    enabled: import.meta.env.MODE === 'development',
    slowThreshold: 1000,
  },
  filters: {
    exclude: [],
    include: [],
    modules: {},
  },
};

// Environment-specific configurations
export const ENVIRONMENT_CONFIGS: Record<string, Partial<LoggingConfig>> = {
  development: {
    level: 'debug',
    console: {
      enabled: true,
      includeTimestamp: true,
      includeLevel: true,
    },
    storage: {
      enabled: true,
      maxEntries: 2000,
      persist: true,
      persistKey: 'coldsea_deploy_logs_dev',
    },
    performance: {
      enabled: true,
      slowThreshold: 500,
    },
  },
  production: {
    level: 'warn',
    console: {
      enabled: false,
      includeTimestamp: true,
      includeLevel: true,
    },
    storage: {
      enabled: true,
      maxEntries: 500,
      persist: false,
      persistKey: 'coldsea_deploy_logs',
    },
    remote: {
      enabled: true,
      batchSize: 50,
      flushInterval: 60000, // 1 minute
    },
    performance: {
      enabled: false,
      slowThreshold: 2000,
    },
  },
  test: {
    level: 'error',
    console: {
      enabled: false,
      includeTimestamp: true,
      includeLevel: true,
    },
    storage: {
      enabled: false,
      maxEntries: 0,
      persist: false,
      persistKey: 'coldsea_deploy_logs_test',
    },
    remote: {
      enabled: false,
    },
    performance: {
      enabled: false,
      slowThreshold: 5000,
    },
  },
};

/**
 * Get the configuration for the current environment
 */
export function getEnvironmentConfig(): LoggingConfig {
  const env = (import.meta.env.MODE as unknown as string | undefined) || 'development';
  const envConfig = ENVIRONMENT_CONFIGS[env] || {};

  // Merge with default config
  return deepMerge(DEFAULT_CONFIG, envConfig);
}

/**
 * Deep merge two objects
 */
function deepMerge(target: unknown, source: unknown): LoggingConfig {
  const output = { ...(target as Record<string, unknown>) };

  if (isObject(target) && isObject(source)) {
    const sourceObj = source as Record<string, unknown>;
    const targetObj = target as Record<string, unknown>;

    Object.keys(sourceObj).forEach((key) => {
      if (isObject(sourceObj[key])) {
        if (!(key in targetObj)) {
          output[key] = sourceObj[key];
        } else {
          output[key] = deepMerge(targetObj[key], sourceObj[key]);
        }
      } else {
        output[key] = sourceObj[key];
      }
    });
  }

  return output as unknown as LoggingConfig;
}

function isObject(item: unknown): item is Record<string, unknown> {
  return item !== null && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Create a custom configuration by merging with defaults
 */
export function createConfig(customConfig: Partial<LoggingConfig>): LoggingConfig {
  return deepMerge(getEnvironmentConfig(), customConfig);
}

/**
 * Validate a log level
 */
export function isValidLogLevel(level: string): level is LogLevel {
  return level in LOG_LEVELS;
}

/**
 * Check if a log level should be logged based on configured minimum level
 */
export function shouldLog(level: LogLevel, minLevel: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[minLevel];
}
